/**
 * Hook para buscar e gerenciar atividades dos deals de um cliente
 * Usado na aba Atividades do modal premium de cliente (modo legado / sem cursor)
 *
 * @version 3.0.0
 * RELEASE: 2026-05-10 v3.0.0 - 100% server-side: lista via RPC `get_customer_activities_legacy`
 *   e mutations via RPCs `complete_deal_activity`, `reopen_deal_activity`, `delete_deal_activity`.
 *   Removidos SELECT/UPDATE/DELETE diretos em crm_customers, crm_deals e crm_deal_activities.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

// Logger do módulo
const logger = createLogger('CustomerActivities');

/**
 * Interface de anexo da atividade (compatível com ActivityAttachmentsDisplay)
 */
export interface CustomerActivityAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number | null;
  public_url: string;
  thumbnail_url: string | null;
  mime_type: string | null;
}

/**
 * Interface da atividade retornada
 */
export interface CustomerActivity {
  id: string;
  activity_type: string;
  title: string;
  description: string | null;
  scheduled_at: string | null;
  completed_at: string | null;
  is_system: boolean;
  created_at: string;
  deal: {
    id: string;
    title: string;
  } | null;
  performed_by_user: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  attachments?: CustomerActivityAttachment[];
  comments_count?: number;
  conferencing_link?: string | null;
  // v3.1.0 — payload estruturado (charge_generated, playbook, task-system)
  new_value?: Record<string, unknown> | null;
}

/** Input para atualização parcial de atividade manual do cliente */
export interface UpdateCustomerActivityInput {
  activity_id: string;
  title?: string;
  description?: string | null;
  scheduled_at?: string | null;
}

/** Opções do hook para lazy loading */
interface CustomerActivitiesOptions {
  enabled?: boolean;
  limit?: number;
}

/**
 * Hook server-side: lista atividades do cliente e expõe mutations via RPCs.
 */
export function useCustomerActivities(
  customerId: string | null,
  options?: CustomerActivitiesOptions
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const limit = options?.limit ?? 50;

  // Lazy loading: só executa quando habilitado e com IDs válidos
  const isEnabled = (options?.enabled ?? true) && !!customerId && !!currentTenant?.id;

  // Query principal: tudo via RPC get_customer_activities_legacy
  const activitiesQuery = useQuery({
    queryKey: ['customer-activities', customerId, limit],
    queryFn: async () => {
      if (!customerId || !currentTenant?.id) return [];

      logger.debug('Buscando atividades do cliente via RPC:', customerId);

      // RPC retorna shape flat (mesmo padrão da paginada)
      const { data, error } = await supabase.rpc('get_customer_activities_legacy', {
        p_customer_id: customerId,
        p_limit: limit,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao buscar atividades:', error.message);
        throw error;
      }

      // Converte shape flat da RPC em CustomerActivity
      const activities: CustomerActivity[] = ((data as any[]) || []).map((row: any) => ({
        id: row.id,
        activity_type: row.activity_type,
        title: row.title,
        description: row.description,
        scheduled_at: row.scheduled_at,
        completed_at: row.completed_at,
        is_system: row.is_system,
        created_at: row.created_at,
        deal: row.deal_id ? { id: row.deal_id, title: row.deal_title || '' } : null,
        performed_by_user: row.performed_by_id ? {
          id: row.performed_by_id,
          full_name: row.performed_by_name,
          avatar_url: row.performed_by_avatar,
        } : null,
        comments_count: row.comments_count || 0,
        conferencing_link: row.conferencing_link || null,
        new_value: row.new_value ?? null, // v3.1.0
        attachments: [],
      }));

      // Anexos via RPC batch (mantém arquitetura server-side)
      if (activities.length > 0) {
        const activityIds = activities.map(a => a.id);
        const { data: attachmentsData, error: attErr } = await supabase.rpc(
          'get_activity_attachments_batch',
          { p_activity_ids: activityIds, p_tenant_hint: currentTenant.id }
        );
        if (attErr) {
          logger.error('Erro ao buscar anexos:', attErr.message);
        } else if (attachmentsData?.length) {
          const byActivity = new Map<string, CustomerActivityAttachment[]>();
          for (const att of attachmentsData as any[]) {
            const list = byActivity.get(att.activity_id) || [];
            list.push({
              id: att.id,
              file_name: att.file_name,
              file_type: att.file_type,
              file_size: att.file_size,
              public_url: att.public_url,
              thumbnail_url: att.thumbnail_url,
              mime_type: att.mime_type,
            });
            byActivity.set(att.activity_id, list);
          }
          for (const act of activities) {
            act.attachments = byActivity.get(act.id) || [];
          }
        }
      }

      logger.debug(`Encontradas ${activities.length} atividades para o cliente`);
      return activities;
    },
    enabled: isEnabled,
    staleTime: 30 * 1000,
  });

  /** Invalida caches de atividades do cliente */
  const invalidateCaches = () => {
    queryClient.invalidateQueries({ queryKey: ['customer-activities', customerId] });
    queryClient.invalidateQueries({ queryKey: ['customer-activities-paginated', customerId] });
    queryClient.invalidateQueries({ queryKey: ['crm-activities-feed'] });
  };

  // Mutation: concluir atividade (RPC)
  const completeActivity = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase.rpc('complete_deal_activity', {
        p_activity_id: activityId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateCaches();
      toast.success('Atividade concluída!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao concluir atividade:', error.message);
      toast.error(`Erro ao concluir atividade: ${error.message}`);
    },
  });

  // Mutation: reabrir atividade (RPC)
  const reopenActivity = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase.rpc('reopen_deal_activity', {
        p_activity_id: activityId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateCaches();
      toast.success('Atividade reaberta!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao reabrir atividade:', error.message);
      toast.error(`Erro ao reabrir atividade: ${error.message}`);
    },
  });

  // Mutation: atualizar atividade manual (RPC update_deal_activity)
  // v3.1.0 - Paridade com useCrmDealActivities.updateActivity
  const updateActivity = useMutation({
    mutationFn: async (input: UpdateCustomerActivityInput) => {
      const payload: Record<string, unknown> = {};
      if (input.title !== undefined) payload.title = input.title;
      if (input.description !== undefined) payload.description = input.description;
      if (input.scheduled_at !== undefined) {
        payload.scheduled_at = input.scheduled_at
          ? new Date(input.scheduled_at).toISOString()
          : null;
      }
      const { error } = await supabase.rpc('update_deal_activity', {
        p_activity_id: input.activity_id,
        p_payload: payload as any,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateCaches();
      toast.success('Atividade atualizada!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao atualizar atividade:', error.message);
      toast.error(`Erro ao atualizar atividade: ${error.message}`);
    },
  });

  // Mutation: remover atividade (RPC)
  const deleteActivity = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase.rpc('delete_deal_activity', {
        p_activity_id: activityId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateCaches();
      toast.success('Atividade removida!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao remover atividade:', error.message);
      toast.error(`Erro ao remover atividade: ${error.message}`);
    },
  });

  return {
    data: activitiesQuery.data,
    isLoading: activitiesQuery.isLoading,
    error: activitiesQuery.error,
    refetch: activitiesQuery.refetch,
    completeActivity,
    reopenActivity,
    updateActivity,
    deleteActivity,
  };
}
